import { useSelector } from 'react-redux';
import Route from '../../../routing/Route';
import Router from '../../../routing/Router';
import { Exam } from '../../../types';
import ExamDelegateSection from '../forms/sections/ExamDelegateSection';
import { EXAM_BASE_ROUTE } from '../index';
import { UserState } from '../../../reducers/user';
import ExamChangeLogs from '../components/ExamChangeLogs';
import ExamDeclarationList from '../components/ExamDeclarationList';
import ExamReportTab from '../components/ExamReportTab';
import UpdateExamLines from '../components/UpdateExamLines';
import { examHasStarted } from '../utils';
import { ExamFormType } from '../types';

const useExamTabs = (exam: Exam | null) => {
  const { account, roleView } = useSelector(
    (selector: { user: UserState }) => ({
      roleView: selector.user.roleView,
      account: selector.user.account,
    }),
  );

  if (!exam || !account) {
    return null;
  }

  const hasStarted = examHasStarted(exam);
  const isAdmin = account && account.roles.includes('ROLE_ADMIN');

  const router = new Router();

  router.addRoute(
    new Route(
      'Gedelegeerden',
      `${EXAM_BASE_ROUTE}/${exam.id}/gedelegeerden`,
      <ExamDelegateSection exam={exam} withActions />,
    ).role(['ROLE_ADMIN']),
  );

  if (!hasStarted) {
    router.addRoute(
      new Route(
        "Diploma's",
        `${EXAM_BASE_ROUTE}/${exam.id}/diplomas`,
        <UpdateExamLines exam={exam} />,
      ).role(['ROLE_ADMIN', 'ROLE_EXAMINER']),
    );
  }

  // the reports become available on the day of the exam
  if (hasStarted) {
    if (exam.delegate) {
      router.addRoute(
        new Route(
          'Gedelegeerdenrapport',
          `${EXAM_BASE_ROUTE}/${exam.id}/gedelegeerdenrapport`,
          <ExamReportTab exam={exam} type={ExamFormType.DELEGATE} />,
        ).role([
          'ROLE_ADMIN',
          'ROLE_LESSON_LOCATION_USER',
          'ROLE_DELEGATE',
          'ROLE_OPERATOR_USER',
        ]),
      );
    }

    if (
      exam.delegate &&
      exam.aspirant &&
      (account.roles.includes('ROLE_ADMIN') || account.id === exam.delegate.id)
    ) {
      router.addRoute(
        new Route(
          'Aspiranten beoordelingsrapport',
          `${EXAM_BASE_ROUTE}/${exam.id}/aspirantenbeoordelingsrapport`,
          <ExamReportTab exam={exam} type={ExamFormType.ASPIRANT} />,
        ).role(['ROLE_ADMIN', 'ROLE_DELEGATE']),
      );
    }

    router.addRoute(
      new Route(
        'Examinatorrapport',
        `${EXAM_BASE_ROUTE}/${exam.id}/examinatorrapport`,
        <ExamReportTab exam={exam} type={ExamFormType.EXAMINER} />,
      ).role([
        'ROLE_ADMIN',
        'ROLE_LESSON_LOCATION_USER',
        'ROLE_OPERATOR_USER',
        'ROLE_EXAMINER',
        'ROLE_DELEGATE',
      ]),
    );
  }

  if (
    isAdmin ||
    exam.delegate?.id === account.id ||
    exam.aspirant?.id === account.id
  ) {
    router.addRoute(
      new Route(
        'Declaraties',
        `${EXAM_BASE_ROUTE}/${exam.id}/declaraties`,
        <ExamDeclarationList exam={exam} />,
      ).role(['ROLE_ADMIN', 'ROLE_DELEGATE']),
    );
  }

  router.addRoute(
    new Route(
      'Logboek',
      `${EXAM_BASE_ROUTE}/${exam.id}/logboek`,
      <ExamChangeLogs exam={exam} />,
    ).role(['ROLE_SUPER_ADMIN']),
  );

  return router.processTabs(roleView);
};

export default useExamTabs;
